import { useEffect, useState } from "react";
import { api, type PlayerProfile } from "./api";
import { Dialog, Field, Notice } from "./ui";

export function Players({
  done,
  canManage,
}: {
  done: () => void;
  canManage: boolean;
}) {
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState<PlayerProfile | "new" | null>(null);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    api<{ players: PlayerProfile[] }>("/players")
      .then(({ players }) => {
        if (current) setPlayers(players);
      })
      .catch((e: Error) => {
        if (current) setError(e.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [reload]);
  const filtered = players.filter((p) =>
    `${p.name} ${p.id}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );
  return (
    <section className="editor stack">
      <div className="section-heading">
        <h1>Players & points</h1>
        <button className="text-button" onClick={done}>
          Back to tournaments
        </button>
      </div>
      <p>
        Points come from tournament results and expire one year after the award.
        New players start at zero.
      </p>
      <div className="player-toolbar">
        <Field label="Find a player">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        {canManage && (
          <button className="primary" onClick={() => setEditing("new")}>
            Add player
          </button>
        )}
      </div>
      {message && <Notice>{message}</Notice>}
      {error && (
        <Notice error>
          {error}{" "}
          <button
            className="text-button"
            onClick={() => setReload((n) => n + 1)}
          >
            Retry
          </button>
        </Notice>
      )}
      {loading ? (
        <Notice>Loading players…</Notice>
      ) : (
        !error &&
        (filtered.length ? (
          <ul className="player-directory">
            {filtered.map((player) => (
              <li key={player.id}>
                <div>
                  <strong>{player.name}</strong>
                  <small>
                    {player.tournamentCount}{" "}
                    {player.tournamentCount === 1 ? "tournament" : "tournaments"}{" "}
                    · ID{" "}
                    {player.id.slice(-8)}
                    {canManage && (
                      <> · {player.accountEmail ?? "No account linked"}</>
                    )}
                  </small>
                </div>
                <span className="player-points">
                  {player.points.toLocaleString()} pts
                </span>
                {canManage && (
                  <button
                    className="text-button"
                    aria-label={`Edit ${player.name}`}
                    onClick={() => setEditing(player)}
                  >
                    Edit
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>
            {players.length
              ? "No matching players. Try another name or add a player."
              : canManage
                ? "No players yet. Add your first player to start building teams."
                : "No players yet."}
          </p>
        ))
      )}
      {editing && (
        <PlayerEditor
          key={editing === "new" ? "new" : editing.id}
          player={editing === "new" ? undefined : editing}
          close={() => setEditing(null)}
          changed={() => setReload((n) => n + 1)}
          saved={(message) => {
            setEditing(null);
            setMessage(message);
            setReload((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}

function PlayerEditor({
  player,
  close,
  saved,
  changed,
}: {
  player?: PlayerProfile;
  close: () => void;
  saved: (message: string) => void;
  changed: () => void;
}) {
  const [name, setName] = useState(player?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <Dialog
      title={player ? "Edit player" : "Add player"}
      onClose={() => {
        if (!busy) close();
      }}
    >
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy || confirmDelete) return;
          setBusy(true);
          setError("");
          try {
            const { player: result } = await api<{ player: PlayerProfile }>(
              player ? `/players/${player.id}` : "/players",
              { name: name.trim() },
              player ? "PUT" : "POST",
            );
            saved(`${result.name} ${player ? "updated" : "added"}.`);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Full name">
          <input
            name="playerName"
            autoComplete="off"
            required
            maxLength={120}
            value={name}
            disabled={busy || confirmDelete}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <p>
          <strong>{(player?.points ?? 0).toLocaleString()} points</strong> ·
          Calculated from results
        </p>
        {player && (
          <p className="muted">
            Name changes apply to the player list and future team selections.
            Published tournament records stay unchanged.
          </p>
        )}
        {error && <Notice error>{error}</Notice>}
        {confirmDelete ? (
          <div className="stack">
            <p>Delete {player?.name} permanently?</p>
            <div className="actions">
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={async () => {
                  if (!player || busy) return;
                  setBusy(true);
                  setError("");
                  try {
                    await api(`/players/${player.id}`, {}, "DELETE");
                    saved(`${player.name} deleted.`);
                  } catch (e) {
                    setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Deleting…" : "Delete player"}
              </button>
              <button
                type="button"
                className="text-button"
                disabled={busy}
                onClick={() => setConfirmDelete(false)}
              >
                Keep player
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="actions">
              <button className="primary" disabled={busy || !name.trim()}>
                {busy ? "Saving…" : player ? "Save player" : "Add player"}
              </button>
              <button
                type="button"
                className="text-button"
                disabled={busy}
                onClick={close}
              >
                Cancel
              </button>
            </div>
            {player &&
              (player.tournamentCount ? (
                <small>
                  This player is used in a tournament and cannot be deleted.
                </small>
              ) : (
                <button
                  type="button"
                  className="text-button danger-text"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete player
                </button>
              ))}
          </>
        )}
      </form>
      {player && !confirmDelete && (
        <PlayerAccount player={player} changed={changed} />
      )}
    </Dialog>
  );
}

function PlayerAccount({
  player,
  changed,
}: {
  player: PlayerProfile;
  changed: () => void;
}) {
  const [linked, setLinked] = useState(player.accountEmail ?? null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const path = `/admin/players/${player.id}/account`;
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      changed();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="stack player-account"
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          const result = await api<{ email: string }>(path, {
            email: linked ?? email,
          });
          setLinked(result.email);
          setEmail("");
          setMessage(`Invite sent to ${result.email}.`);
        });
      }}
    >
      <h3>Account</h3>
      {linked ? (
        <>
          <p>
            Linked to <strong>{linked}</strong>. This person sees these results
            in their profile and can score their own matches.
          </p>
          <div className="actions">
            <button className="secondary" disabled={busy}>
              {busy ? "Sending…" : "Send the invite again"}
            </button>
            <button
              type="button"
              className="text-button danger-text"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api(path, {}, "DELETE");
                  setLinked(null);
                  setMessage("Account unlinked. Results stay with the player.");
                })
              }
            >
              Unlink account
            </button>
          </div>
        </>
      ) : (
        <>
          <Field
            label="Invite by email"
            hint="Links this record to the person's account and emails them a sign-in link."
          >
            <input
              type="email"
              autoComplete="off"
              required
              maxLength={254}
              value={email}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <div className="actions">
            <button className="secondary" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Link and send invite"}
            </button>
          </div>
        </>
      )}
      {message && <Notice>{message}</Notice>}
      {error && <Notice error>{error}</Notice>}
    </form>
  );
}
